/**
 * @module AuroraProductZoom
 * @description Singleton module that manages Aurora Product Zoom on PDP.
 */
const AuroraProductZoom = (function () {
  /**
   * An object representing the module's settings.
   *
   * @private
   * @type {object} SETTINGS
   * @description Contains initial configuration for the zoom functionality.
   */
  const SETTINGS = {
    name: "aurora-product-zoom.js",
    debugging: true,
    landscapeAspectRatio: 4 / 3,
    zoomLevel: 3,
    zoomAreaScaleFactor: 1.5,
  };

  /**
   * Cached references to important HTML elements used by the module.
   *
   * @private
   * @type {Object<string, HTMLElement>} ELEMENTS
   */
  const ELEMENTS = {
    clickToEnlarge: document.querySelector(".js--click-to-enlarge"),
    closeModal: document.querySelector(".js--close-modal"),
    imgContainer: document.querySelector(".js--img-container"),
    zoomContainer: document.querySelector(".js--product-zoom-container")
  };

  /**
   * Initializes the module by setting up modal behavior and zoom functionality.
   *
   * @public
   * @function init
   */
  const init = function () {
    if (SETTINGS.debugging) console.log(`${SETTINGS.name} :: Start`);
    setupModal();
    setupZoom();

    if (SETTINGS.debugging) console.log(`${SETTINGS.name} :: Complete`);
  };

  /**
   * Sets up the click-to-open and close modal functionality for product images.
   *
   * @private
   * @function setupModal
   */
  const setupModal = function () {
    if (!ELEMENTS.clickToEnlarge || !ELEMENTS.closeModal || !ELEMENTS.imgContainer) {
      return;
    }
    ELEMENTS.clickToEnlarge.addEventListener("click", (e) => {
      e.preventDefault();
      document.body.classList.add("no-scroll");
      ELEMENTS.imgContainer.classList.add("js--img-modal");
    });

    ELEMENTS.closeModal.addEventListener("click", (e) => {
      e.preventDefault();
      document.body.classList.remove("no-scroll");
      ELEMENTS.imgContainer.classList.remove("js--img-modal");
    });
  };

  /**
   * Sets up hover zoom functionality for product images on desktop devices.
   *
   * @private
   * @function setupZoom
   */
  const setupZoom = function () {
    if (!window.matchMedia("(pointer: fine)").matches) return;

    const { landscapeAspectRatio, zoomLevel, zoomAreaScaleFactor } = SETTINGS;
    const imageSlides = document.querySelectorAll(".embla__image--main");
    const zoomContainer = ELEMENTS.zoomContainer;
    const rightColumn = document.querySelector(".aur-cell.med-66");

    if (!imageSlides.length || !zoomContainer || !rightColumn) {
      if (SETTINGS.debugging) console.warn(`${SETTINGS.name} :: Zoom ELEMENTS not found`);
      return;
    }

    imageSlides.forEach((slide) => {
      const smallImg = slide.querySelector("img");
      const lens = slide.querySelector(".js--product-zoom-lens");
      let containedImg = {};

      /**
       * Hides the zoom container and lens when not hovering over the image.
       *
       * @private
       * @function hideZoom
       */
      const hideZoom = () => {
        zoomContainer.style.opacity = "0";
        zoomContainer.style.visibility = "hidden";
        if (lens) lens.style.display = "none";
      };

      slide.addEventListener("mouseenter", () => {
        const containerW = smallImg.offsetWidth;
        const containerH = smallImg.offsetHeight;
        const imageW = smallImg.naturalWidth;
        const imageH = smallImg.naturalHeight;
        const containerRatio = containerW / containerH;
        const imageRatio = imageW / imageH;

        if (imageRatio > containerRatio) {
          containedImg.width = containerW;
          containedImg.height = containerW / imageRatio;
          containedImg.x = 0;
          containedImg.y = (containerH - containedImg.height) / 2;
        } else {
          containedImg.height = containerH;
          containedImg.width = containerH * imageRatio;
          containedImg.y = 0;
          containedImg.x = (containerW - containedImg.width) / 2;
        }

        const imgRect = smallImg.getBoundingClientRect();
        const rightColRect = rightColumn.getBoundingClientRect();
        const sectionRect = zoomContainer.closest("section").getBoundingClientRect();

        const desiredZoomWidth = imgRect.width * zoomAreaScaleFactor;
        const maxZoomWidth = window.innerWidth * 0.6;
        const finalZoomWidth = Math.min(desiredZoomWidth, maxZoomWidth);
        const finalZoomHeight = finalZoomWidth / landscapeAspectRatio;
        const zoomTop = imgRect.top - sectionRect.top + imgRect.height / 2 - finalZoomHeight / 2;

        zoomContainer.style.width = `${finalZoomWidth}px`;
        zoomContainer.style.height = `${finalZoomHeight}px`;
        zoomContainer.style.top = `${zoomTop}px`;
        zoomContainer.style.left = `${rightColRect.left - sectionRect.left}px`;

        const lensWidth = finalZoomWidth / zoomLevel;
        const lensHeight = finalZoomHeight / zoomLevel;
        if (lens) {
          lens.style.width = `${lensWidth}px`;
          lens.style.height = `${lensHeight}px`;
        }

        const bgWidth = containedImg.width * zoomLevel;
        const bgHeight = bgWidth / (imageW / imageH);
        zoomContainer.style.backgroundImage = `url('${smallImg.src}')`;
        zoomContainer.style.backgroundSize = `${bgWidth}px ${bgHeight}px`;
      });

      slide.addEventListener("mouseleave", hideZoom);

      slide.addEventListener("mousemove", (e) => {
        const rect = smallImg.getBoundingClientRect();
        let cursorX = e.clientX - rect.left;
        let cursorY = e.clientY - rect.top;

        if (
          cursorX < containedImg.x ||
          cursorX > containedImg.x + containedImg.width ||
          cursorY < containedImg.y ||
          cursorY > containedImg.y + containedImg.height
        ) {
          hideZoom();
          return;
        }

        zoomContainer.style.opacity = "1";
        zoomContainer.style.visibility = "visible";
        if (lens) lens.style.display = "block";

        let x = cursorX - containedImg.x;
        let y = cursorY - containedImg.y;

        let lensX = containedImg.x + x - lens.offsetWidth / 2;
        let lensY = containedImg.y + y - lens.offsetHeight / 2;

        if (lensX < containedImg.x) lensX = containedImg.x;
        if (lensX > containedImg.x + containedImg.width - lens.offsetWidth) lensX = containedImg.x + containedImg.width - lens.offsetWidth;
        if (lensY < containedImg.y) lensY = containedImg.y;
        if (lensY > containedImg.y + containedImg.height - lens.offsetHeight) lensY = containedImg.y + containedImg.height - lens.offsetHeight;

        if (lens) lens.style.transform = `translate(${lensX}px, ${lensY}px)`;

        const bgPosX = -(x * zoomLevel - zoomContainer.offsetWidth / 2);
        const bgPosY = -(y * zoomLevel - zoomContainer.offsetHeight / 2);
        zoomContainer.style.backgroundPosition = `${bgPosX}px ${bgPosY}px`;
      });
    });
  };

  return {
    init,
    SETTINGS,
  };
})();
